/**
 * utils/fiberCableUtilities.js - Utilitaires pour le traitement des câbles fibre
 * Fonctions utilitaires réutilisables pour l'extraction et validation des données
 */
import { readExcelCell } from "./excelReader.js";

import logger from "./logger.js";

/**
 * Vérifier si une valeur est vide ou N/A
 * @param {string} value - Valeur à tester
 * @returns {boolean} true si vide ou N/A
 */
export function isEmptyOrNA(value) {
  if (!value || typeof value !== "string") return true;
  const cleaned = value.trim().toUpperCase();
  return cleaned === "" || cleaned === "N/A" || cleaned === "NA" || cleaned === "#N/A";
}

/**
 * Extraire le BPO depuis l'ID du câble
 * @param {string} cableId - ID du câble
 * @returns {string} BPO extrait ou chaîne vide
 */
export function extractBpoFromCableId(cableId) {
  if (!cableId || typeof cableId !== "string") {
    return "";
  }

  const id = cableId.trim();

  try {
    // Règle 1: BE-SRO-BPI - garder tout avant _B2
    if (id.includes("BE-SRO-BPI")) {
      const b2Index = id.indexOf("_B2");
      return b2Index !== -1 ? id.substring(0, b2Index) : id;
    }

    // Règle 2: PBO - extraire le numéro
    if (id.includes("PBO")) {
      const b2Index = id.indexOf("_B2");
      if (b2Index === -1) return id;

      const beforeB2 = id.substring(0, b2Index);
      const pboMatch = beforeB2.match(/PBO(\d+)/);
      return pboMatch ? pboMatch[1] : id.substring(0, b2Index);
    }

    // Règle 3: BE_ - garder tout avant _B2
    if (id.includes("BE-")) {
      const b2Index = id.indexOf("_B2");
      return b2Index !== -1 ? id.substring(0, b2Index) : id;
    }

    return id;
  } catch (error) {
    return "";
  }
}

/**
 * Extraire la référence courte depuis l'ID du câble (B2 et ce qui suit)
 * @param {string} cableId - ID du câble complet
 * @returns {string} Référence courte ou chaîne vide
 */
export function extractReferenceFromCableId(cableId) {
  if (!cableId || typeof cableId !== "string") {
    return "";
  }

  try {
    const id = cableId.trim();

    // Chercher _B2
    const b2Index = id.indexOf("_B2");
    if (b2Index === -1) {
      return ""; // Pas de _B2 trouvé
    }

    // Prendre B2 et ce qui suit (supprimer le _)
    const reference = id.substring(b2Index + 1); // +1 pour enlever le _

    return reference; // Ex: "B2-T2-MF"
  } catch (error) {
    logger.warn(
      {
        context: "fiber-cable-reference-extraction",
        error: error.message,
        cableId,
        operation: "extract-reference-from-cable-id",
        hasB2Index: cableId?.indexOf("_B2") !== -1,
        b2Index: cableId?.indexOf("_B2") || -1,
      },
      "Erreur extraction référence"
    );
    return "";
  }
}

/**
 * Extraire le tiroir et module depuis l'ID du câble
 * @param {string} cableId - ID du câble
 * @returns {Object} { tiroir: string, module: string }
 */
export function extractTiroirModuleFromCableId(cableId) {
  if (!cableId || typeof cableId !== "string") {
    return { tiroir: "", module: "" };
  }

  try {
    const id = cableId.trim();

    // Chercher _B2
    const b2Index = id.indexOf("_B2");
    if (b2Index === -1) {
      return { tiroir: "", module: "" };
    }

    // Prendre ce qui vient après _B2
    const afterB2 = id.substring(b2Index + 3); // +3 pour passer "_B2"

    // Chercher le pattern T{i}-M{j}
    const pattern = /^-T(\d+)-M([A-Z])$/i; // T suivi de chiffres, M suivi d'une lettre
    const match = afterB2.match(pattern);

    if (match) {
      const tiroir = match[1]; // Le nombre après T
      const module = match[2].toUpperCase(); // La lettre après M
      return { tiroir, module };
    }

    // Pattern alternatif si pas de M: T{i}-{lettre}
    const altPattern = /^-T(\d+)-([A-Z])$/i;
    const altMatch = afterB2.match(altPattern);

    if (altMatch) {
      const tiroir = altMatch[1];
      const module = altMatch[2].toUpperCase();
      return { tiroir, module };
    }

    return { tiroir: "", module: "" };
  } catch (error) {
    logger.warn(
      {
        context: "fiber-cable-tiroir-module-extraction",
        error: error.message,
        cableId,
        operation: "extract-tiroir-module-from-cable-id",
        hasB2Index: cableId?.indexOf("_B2") !== -1,
        b2Index: cableId?.indexOf("_B2") || -1,
        afterB2: cableId?.substring((cableId?.indexOf("_B2") || -1) + 3) || "",
      },
      "Erreur extraction tiroir/module"
    );
    return { tiroir: "", module: "" };
  }
}

/**
 * Trouver la dernière colonne avec des valeurs pour une ligne
 * @param {Object} worksheet - Feuille Excel
 * @param {number} row - Numéro de ligne (1-based)
 * @param {number} startCol - Colonne de départ (0-based)
 * @param {number} maxCol - Colonne max à vérifier
 * @returns {number} Index de la dernière colonne avec valeur (-1 si aucune)
 */
export function findLastColumnWithValue(worksheet, row, startCol, maxCol) {
  let lastCol = -1;

  for (let col = startCol; col < maxCol; col++) {
    const cellAddress = String.fromCharCode(65 + col) + row;
    const cell = worksheet[cellAddress];
    const cellValue = readExcelCell(cell);

    if (cellValue && !isEmptyOrNA(cellValue)) {
      // Vérifier que c'est un nombre (mesure de distance)
      const numValue = parseFloat(cellValue);
      if (!isNaN(numValue) && numValue > 0) {
        lastCol = col;
      }
    }
  }

  return lastCol;
}

/**
 * Extraire les événements d'une ligne
 * @param {Object} worksheet - Feuille Excel
 * @param {number} row - Numéro de ligne
 * @param {number} eventsStartCol - Première colonne d'événements
 * @param {number} maxEventCols - Nombre max de colonnes d'événements
 * @returns {Array} Tableau des valeurs d'événements
 */
export function extractEventValues(worksheet, row, eventsStartCol, maxEventCols) {
  const events = [];

  for (let col = eventsStartCol; col < eventsStartCol + maxEventCols; col++) {
    const cellAddress = String.fromCharCode(65 + col) + row;
    const cell = worksheet[cellAddress];
    const cellValue = readExcelCell(cell);

    if (cellValue && !isEmptyOrNA(cellValue)) {
      const numValue = parseFloat(cellValue);
      if (!isNaN(numValue)) {
        events.push(numValue);
      }
    }
  }

  return events;
}
